// Suoni dell'interfaccia: tocco sui pulsanti, conferma sui salvataggi riusciti,
// sirena sui campi obbligatori non compilati, avviso su errori e richieste di eliminazione.
// I suoni sono sintetizzati con Web Audio (nessun file da scaricare, latenza minima).
// Preferenza per dispositivo in localStorage; di default attivi.
import { toast } from "sonner";

const STORAGE_KEY = "df_ui_sounds";
export const MISSING_FIELDS_EVENT = "df-missing-fields";
const CHANGE_EVENT = "df-ui-sounds-change";
const CLICKABLE = [
  "button", "a[href]", "summary", "label",
  "input[type=checkbox]", "input[type=radio]", "input[type=submit]", "input[type=button]",
  "[role=button]", "[role=menuitem]", "[role=menuitemradio]", "[role=menuitemcheckbox]",
  "[role=tab]", "[role=switch]", "[role=checkbox]", "[role=radio]", "[role=option]",
].join(",");

let ctx = null;
let master = null;
let direct = null;
const lastPlayed = {};

export function soundsEnabled() {
  try { return localStorage.getItem(STORAGE_KEY) !== "off"; } catch { return true; }
}

export function setSoundsEnabled(on) {
  try { localStorage.setItem(STORAGE_KEY, on ? "on" : "off"); } catch { /* storage non disponibile */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
  if (on) { unlock(); play("success"); }
}

export function onSoundsChange(cb) {
  window.addEventListener(CHANGE_EVENT, cb);
  return () => window.removeEventListener(CHANGE_EVENT, cb);
}

// L'AudioContext nasce solo dentro un gesto dell'utente (requisito di Safari/Chrome).
function unlock() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  if (!ctx) {
    ctx = new AC();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    master = ctx.createGain();
    master.gain.value = 0.75;
    master.connect(comp).connect(ctx.destination);
    // Uscita senza compressore, per il tasto: deve risaltare a volume pieno.
    direct = ctx.createGain();
    direct.gain.value = 1;
    direct.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
}

function tone(freq, start, { type = "sine", peak = 0.2, attack = 0.004, decay = 0.4, glideTo, filter, bus } = {}) {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, start + decay * 0.6);
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(peak, start + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, start + attack + decay);
  let out = osc.connect(env);
  if (filter) {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = filter;
    out = out.connect(lp);
  }
  out.connect(bus || master);
  osc.start(start);
  osc.stop(start + attack + decay + 0.05);
}

function noiseTap(start, { freq = 3800, gain = 0.22, q = 1.2, length = 0.02 } = {}) {
  const len = Math.floor(ctx.sampleRate * length);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 3;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = freq;
  bp.Q.value = q;
  const g = ctx.createGain();
  g.gain.value = gain;
  src.connect(bp).connect(g).connect(master);
  src.start(start);
}

// Due gamme: "evidente" (in uso nell'app) e "morbida" (la prima versione, più discreta).
export const PALETTES = {
  evidente: {
    // Tasto: campanella acuta (Mi7) con parziali metalliche e breve risonanza.
    tap(t) {
      noiseTap(t, { freq: 7000, gain: 0.6, q: 1.5, length: 0.006 });
      tone(2637, t, { peak: 0.72, attack: 0.001, decay: 0.45, bus: direct });
      tone(2637 * 2.76, t, { peak: 0.16, attack: 0.001, decay: 0.16, bus: direct });
      tone(2637 * 1.5, t, { peak: 0.12, attack: 0.001, decay: 0.28, bus: direct });
    },
    // Conferma: arpeggio maggiore ascendente a tre note (Sol5 → Do6 → Mi6), brillante.
    success(t) {
      [[784, 0], [1046.5, 0.075], [1318.5, 0.15]].forEach(([f, d], i) => {
        tone(f, t + d, { peak: 0.26, decay: i === 2 ? 0.7 : 0.28 });
        tone(f * 2, t + d, { type: "triangle", peak: 0.06, decay: 0.2 });
      });
    },
    // Avviso / eliminazione: "uh-oh" discendente ben udibile, timbro caldo filtrato.
    alert(t) {
      tone(523, t, { type: "square", peak: 0.16, decay: 0.2, filter: 1900 });
      tone(523, t, { type: "triangle", peak: 0.22, decay: 0.2 });
      tone(370, t + 0.16, { type: "square", peak: 0.18, decay: 0.42, filter: 1500 });
      tone(370, t + 0.16, { type: "triangle", peak: 0.26, decay: 0.42 });
    },
  },
  morbida: {
    // Tasto: "tap" morbido e corto, come un tasto meccanico silenzioso.
    tap(t) {
      noiseTap(t);
      tone(1900, t, { type: "triangle", peak: 0.09, attack: 0.001, decay: 0.035, glideTo: 1200 });
    },
    // Conferma: due note ascendenti a campanella (Do6 → Sol6).
    success(t) {
      tone(1046.5, t, { peak: 0.16, decay: 0.32 });
      tone(2093, t, { peak: 0.03, decay: 0.18 });
      tone(1568, t + 0.09, { peak: 0.18, decay: 0.55 });
      tone(3136, t + 0.09, { peak: 0.025, decay: 0.25 });
    },
    // Avviso / eliminazione: due note discendenti, gravi e smorzate, mai stridenti.
    alert(t) {
      tone(466, t, { type: "triangle", peak: 0.2, decay: 0.22, filter: 1600 });
      tone(349, t + 0.13, { type: "triangle", peak: 0.22, decay: 0.38, filter: 1400 });
    },
  },
};
const ACTIVE_PALETTE = "evidente";

// Sirena: campi obbligatori non compilati. Due cicli sale/scende, ben udibile ma breve.
function siren(t) {
  const end = t + 0.95;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(0.5, t + 0.03);
  env.gain.setValueAtTime(0.5, end - 0.15);
  env.gain.exponentialRampToValueAtTime(0.0001, end);
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2600;
  env.connect(direct);
  lp.connect(env);
  [["triangle", 1], ["square", 0.35]].forEach(([type, level]) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    g.gain.value = level;
    osc.frequency.setValueAtTime(700, t);
    [0.22, 0.44, 0.66, 0.88].forEach((d, i) => osc.frequency.linearRampToValueAtTime(i % 2 ? 700 : 1300, t + d));
    osc.connect(g).connect(lp);
    osc.start(t);
    osc.stop(end + 0.05);
  });
}

// Messaggi che chiedono di compilare un campo (es. "Inserisci…", "Seleziona…", "…obbligatorio").
const MISSING_FIELD = /^(compila|inserisci|seleziona|scegli|indica|completa|fotografa|spunta|rispondi|aggiungi almeno|scrivi il motivo|conferma l)|obbligator|ne mancano|da compilare|non può essere vuot|: completa |: descrivi |in rosso/i;
const MIN_GAP = { siren: 1200 };

export function play(name, palette = ACTIVE_PALETTE) {
  const sound = name === "siren" ? siren : PALETTES[palette]?.[name];
  if (!ctx || !soundsEnabled() || !sound) return;
  if (ctx.state === "closed") return;
  const now = performance.now();
  // Evita raffiche: stesso suono ripetuto a pochi ms (label+input, più toast insieme).
  if (now - (lastPlayed[name] ?? -Infinity) < (MIN_GAP[name] ?? 90)) return;
  lastPlayed[name] = now;
  try { sound(ctx.currentTime + 0.005); } catch { /* audio non disponibile */ }
}

let installed = false;

export function installUiSounds() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const onGesture = () => unlock();
  window.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
  window.addEventListener("keydown", onGesture, { capture: true, passive: true });

  document.addEventListener("click", (e) => {
    if (!e.isTrusted) return;
    unlock();
    const el = e.target instanceof Element ? e.target.closest(CLICKABLE) : null;
    if (!el || el.disabled || el.getAttribute("aria-disabled") === "true") return;
    if (el.closest("[data-sound=off]")) return;
    play("tap");
  }, true);

  const wrap = (method, sound) => {
    const original = toast[method];
    if (typeof original !== "function") return;
    toast[method] = (...args) => { play(typeof sound === "function" ? sound(args[0]) : sound); return original(...args); };
  };
  // Campi mancanti: sirena + avviso al centro dello schermo (mostrato anche a suoni spenti).
  const notifyMissing = (message) => window.dispatchEvent(new CustomEvent(MISSING_FIELDS_EVENT, { detail: { message } }));
  const alertOrSiren = (message) => {
    if (typeof message !== "string" || !MISSING_FIELD.test(message.trim())) return "alert";
    notifyMissing(message);
    return "siren";
  };
  wrap("success", "success");
  wrap("error", alertOrSiren);
  wrap("warning", alertOrSiren);

  // Campi "required" bloccati dal browser al momento dell'invio del modulo.
  document.addEventListener("invalid", () => { notifyMissing(); play("siren"); }, true);

  const nativeConfirm = window.confirm.bind(window);
  window.confirm = (message) => { play("alert"); return nativeConfirm(message); };
}
